import { Text } from 'pixi.js';
import type { Disposable } from '../core/Event';
import { DefaultRandom, type Random } from '../core/Random';
import { Easings, TweenGroup } from '../core/Tween';
import { WinParticles } from '../effects/WinParticles';
import { PlayerProfile } from '../model/profile/PlayerProfile';
import { DEFAULT_THEME, type Theme } from '../theme';
import { BalanceDisplay } from '../widgets/BalanceDisplay';
import { CoinAmountLabel } from '../widgets/CoinAmountLabel';
import { PrimaryButton } from '../widgets/PrimaryButton';
import { SceneBase } from './SceneBase';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';
const SECONDS_PER_DAY = 86_400;

export interface DailyQuestSceneOptions {
  /** Called when the player taps the back-to-map button. */
  onComplete?: () => void;
}

/**
 * Daily-Quest scene — fixed 50 coins + 1 gem on a 24h cooldown,
 * independent of the launch DailyBonusModal (which keeps its own
 * 100-700c streak path; both surfaces can be claimed once per day
 * without colliding).
 *
 * <p>If the player can claim, render a single CLAIM button with the
 * preview reward; on tap, fire a small celebration + flip the button
 * to "BACK TO MAP". If on cooldown, render an HH:MM:SS countdown to
 * the next claim and the back-to-map button is immediately available.
 */
export class DailyQuestScene extends SceneBase {
  private readonly profile: PlayerProfile;
  private readonly onComplete: (() => void) | null;
  private readonly random: Random;
  private readonly tweens = new TweenGroup();

  private balance: BalanceDisplay | null = null;
  private balanceSub: Disposable | null = null;
  private titleText: Text | null = null;
  private subtitleText: Text | null = null;
  private rewardText: CoinAmountLabel | null = null;
  private gemsText: Text | null = null;
  private cooldownText: Text | null = null;
  private actionButton: PrimaryButton | null = null;
  private particles: WinParticles | null = null;
  private cooldownTickerHandler: ((tick: { deltaMS: number }) => void) | null = null;
  private cooldownAccumulatorMs = 0;
  private claimed = false;

  constructor(
    container: HTMLElement,
    theme: Theme = DEFAULT_THEME,
    profile: PlayerProfile,
    options: DailyQuestSceneOptions = {},
  ) {
    super(container, { theme });
    this.profile = profile;
    this.onComplete = options.onComplete ?? null;
    this.random = new DefaultRandom();
  }

  protected buildContent(): void {
    const accent = this.theme.palette.accent;

    this.balance = new BalanceDisplay(this.profile.balance);
    this.app.stage.addChild(this.balance);
    this.balanceSub = this.profile.events.on('balance-changed', (event) => {
      this.balance?.setAmount(event.after);
      this.balance?.bump();
    });

    this.particles = new WinParticles(this.app.ticker);
    this.app.stage.addChild(this.particles);

    this.titleText = this.buildText('DAILY QUEST', 30, 0xffffff);
    this.app.stage.addChild(this.titleText);

    this.subtitleText = this.buildText('', 16, 0xffffff, 0.75);
    this.app.stage.addChild(this.subtitleText);

    this.rewardText = new CoinAmountLabel('', {
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 32,
        fontWeight: '900',
        fill: 0xfff59d,
        align: 'center',
        letterSpacing: 2,
      },
    });
    this.rewardText.alpha = 0;
    this.app.stage.addChild(this.rewardText);

    this.gemsText = this.buildText('', 22, 0x9be7ff);
    this.gemsText.alpha = 0;
    this.app.stage.addChild(this.gemsText);

    this.cooldownText = this.buildText('', 22, 0xffffff, 0.85);
    this.app.stage.addChild(this.cooldownText);

    this.actionButton = new PrimaryButton({
      label: 'CLAIM',
      width: 240,
      fillActive: accent,
      fillHover: darken(accent, 0.7),
      textColor: 0xffffff,
      onTap: () => void this.handleAction(),
    });
    this.app.stage.addChild(this.actionButton);

    this.refreshState();
    this.layout();
  }

  protected onResize(): void {
    this.layout();
  }

  protected disposeResources(wasInitialized: boolean): void {
    this.balanceSub?.dispose();
    this.balanceSub = null;
    this.tweens.cancelAll();
    if (this.cooldownTickerHandler) {
      this.app.ticker.remove(this.cooldownTickerHandler);
      this.cooldownTickerHandler = null;
    }
    if (!wasInitialized) return;
    this.balance?.destroyDisplay();
    this.balance?.destroy({ children: true });
    this.balance = null;
    this.particles?.destroyEffect();
    this.particles?.destroy({ children: true });
    this.particles = null;
    this.titleText?.destroy();
    this.titleText = null;
    this.subtitleText?.destroy();
    this.subtitleText = null;
    this.rewardText?.destroy();
    this.rewardText = null;
    this.gemsText?.destroy();
    this.gemsText = null;
    this.cooldownText?.destroy();
    this.cooldownText = null;
    this.actionButton?.destroy({ children: true });
    this.actionButton = null;
  }

  /** Update titleText / cooldown / button label based on whether the
   * player can claim right now. Called once on init and again after a
   * successful claim. */
  private refreshState(): void {
    if (!this.subtitleText || !this.actionButton || !this.cooldownText) return;
    const canClaim = this.profile.canClaimDailyQuest();
    if (canClaim) {
      this.subtitleText.text = 'Claim today’s reward';
      this.actionButton.setLabel('CLAIM 50 + 1 GEM');
      this.cooldownText.text = '';
      this.stopCooldownTicker();
      return;
    }
    // On cooldown — show countdown + flip button to "BACK TO MAP".
    this.subtitleText.text = 'Already claimed today';
    this.actionButton.setLabel('BACK TO MAP');
    this.startCooldownTicker();
  }

  private startCooldownTicker(): void {
    this.updateCooldownLabel();
    if (this.cooldownTickerHandler) return;
    // Update once per second — no need for per-frame precision on a
    // human-readable HH:MM:SS countdown.
    this.cooldownAccumulatorMs = 0;
    this.cooldownTickerHandler = (tick: { deltaMS: number }) => {
      this.cooldownAccumulatorMs += tick.deltaMS;
      if (this.cooldownAccumulatorMs >= 1000) {
        this.cooldownAccumulatorMs -= 1000;
        this.updateCooldownLabel();
      }
    };
    this.app.ticker.add(this.cooldownTickerHandler);
  }

  private stopCooldownTicker(): void {
    if (!this.cooldownTickerHandler) return;
    this.app.ticker.remove(this.cooldownTickerHandler);
    this.cooldownTickerHandler = null;
  }

  private updateCooldownLabel(): void {
    if (!this.cooldownText) return;
    const remaining = this.profile.lastDailyQuestEpoch + SECONDS_PER_DAY - nowEpoch();
    if (remaining <= 0) {
      // Cooldown elapsed mid-scene — flip back to claimable. Rare but
      // possible if the player sat on this scene for 24h.
      this.refreshState();
      return;
    }
    this.cooldownText.text = `Next claim in ${formatHms(remaining)}`;
  }

  private async handleAction(): Promise<void> {
    if (this.claimed) {
      this.onComplete?.();
      return;
    }
    if (!this.profile.canClaimDailyQuest()) {
      this.onComplete?.();
      return;
    }
    const result = this.profile.claimDailyQuest();
    if (!result) {
      this.onComplete?.();
      return;
    }
    this.claimed = true;
    this.fireBurst();
    await this.revealRewardLabels(result);
    this.refreshState();
  }

  private fireBurst(): void {
    if (!this.particles) return;
    const accent = this.theme.palette.accent;
    const warm = this.theme.palette.warm;
    this.particles.burst(80, {
      tints: [warm, accent, 0xfff59d, 0x9be7ff, 0xffe066],
      lifeMs: 1200,
      minSpeed: 200,
      maxSpeed: 480,
      gravity: 600,
      radius: 4,
    });
    // Light random jitter via the random source so reused scene
    // instances don't get an identical burst pattern (cosmetic).
    void this.random;
  }

  private revealRewardLabels(result: { coins: number; gems: number }): Promise<void> {
    if (!this.rewardText || !this.gemsText) return Promise.resolve();
    this.rewardText.setText(`+${result.coins.toLocaleString()}`);
    this.gemsText.text = `+${result.gems} 💎`;
    return Promise.all([
      this.tweens.startAsync({
        durationMs: 360,
        easing: Easings.easeOutBack,
        onUpdate: (p) => {
          if (!this.rewardText) return;
          this.rewardText.alpha = p;
          this.rewardText.scale.set(0.7 + p * 0.3);
        },
      }),
      this.tweens.startAsync({
        durationMs: 360,
        easing: Easings.easeOutBack,
        onUpdate: (p) => {
          if (!this.gemsText) return;
          this.gemsText.alpha = p;
          this.gemsText.scale.set(0.7 + p * 0.3);
        },
      }),
    ]).then(() => undefined);
  }

  private layout(): void {
    if (!this.balance || !this.titleText || !this.subtitleText) return;
    if (!this.rewardText || !this.gemsText || !this.cooldownText) return;
    if (!this.actionButton || !this.particles) return;

    const { width, height } = this.app.renderer;
    const centerX = Math.round(width / 2);
    const centerY = Math.round(height / 2);

    this.balance.x = centerX;
    this.balance.y = 16;

    this.titleText.x = centerX;
    this.titleText.y = centerY - 100;

    this.subtitleText.x = centerX;
    this.subtitleText.y = centerY - 60;

    this.particles.x = centerX;
    this.particles.y = centerY - 10;

    this.rewardText.x = centerX;
    this.rewardText.y = centerY - 10;

    this.gemsText.x = centerX;
    this.gemsText.y = centerY + 32;

    this.cooldownText.x = centerX;
    this.cooldownText.y = centerY + 60;

    this.actionButton.x = centerX;
    this.actionButton.y = Math.min(height - 70, centerY + 130);
  }

  private buildText(text: string, size: number, fill: number, alpha = 1): Text {
    const t = new Text({
      text,
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: size,
        fontWeight: '900',
        fill,
        align: 'center',
        letterSpacing: size >= 28 ? 2 : 1,
      },
    });
    t.anchor.set(0.5);
    t.alpha = alpha;
    return t;
  }
}

function nowEpoch(): number {
  return Math.floor(Date.now() / 1000);
}

function formatHms(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hh = Math.floor(s / 3600).toString().padStart(2, '0');
  const mm = Math.floor((s % 3600) / 60).toString().padStart(2, '0');
  const ss = (s % 60).toString().padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

function darken(color: number, factor: number): number {
  const r = Math.round(((color >> 16) & 0xff) * factor);
  const g = Math.round(((color >> 8) & 0xff) * factor);
  const b = Math.round((color & 0xff) * factor);
  return (r << 16) | (g << 8) | b;
}
